import store from '../store/store';
import { addOfflineFile, setSyncing, removeFile } from '../store/CodeVerificationSlice';

/**
 * SharePoint site + document library (same pattern as your reference uploadFileToSharePoint).
 * SP_LIB_NAME is used in: contextinfo → GetFolderByServerRelativeUrl('…') → Files/add → getbytitle → MERGE QualityId
 */
export const SP_SITE_URL = 'https://bectors.sharepoint.com/sites/PTMS_PRD';
/** Library name for REST (matches getbytitle) — short form like your SP_LIB_NAME */
export const SP_LIB_NAME = 'CodeVerificationDocuments';
const SP_LIBRARY_TITLE = SP_LIB_NAME;
const FOLDER_SERVER_RELATIVE_URL = '/sites/PTMS_PRD/CodeVerificationDocuments';

/** Prefer short library name (reference API); fall back to full server-relative path. */
const UPLOAD_FOLDER_CANDIDATES = [SP_LIB_NAME, FOLDER_SERVER_RELATIVE_URL] as const;

function buildAbsoluteFileUrl(serverRelativeUrl: string): string {
  const path = (serverRelativeUrl || '').trim();
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const origin = new URL(SP_SITE_URL).origin;
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Match your reference: `attachment-${cycleNum}-${rowIndex}` first, then legacy ids.
 */
function getCodeVerificationFileInput(cycleNum: number, rowIndex?: number): HTMLInputElement | null {
  const r = rowIndex ?? 0;
  const candidates = [
    `attachment-${cycleNum}-${r}`,
    `code-verification-attachment-${cycleNum}-${r}`,
    `code-verification-attachment-${cycleNum}`,
  ];
  for (const id of candidates) {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) return el;
  }
  return null;
}

/** Viewer/open URL — same idea as buildSpViewerUrl: absolute URL from upload response. */
function buildSpViewerUrl(serverRelativeUrl: string | undefined, fileName: string): string {
  if (serverRelativeUrl && String(serverRelativeUrl).trim()) {
    return buildAbsoluteFileUrl(serverRelativeUrl);
  }
  return buildAbsoluteFileUrl(`${FOLDER_SERVER_RELATIVE_URL}/${fileName}`);
}

// Interface for save response
export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Interface for file display data
export interface FileDisplayData {
  id: string;
  cycleNum: number;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  uploadTime: number;
  isOffline: boolean;
}

// Interface for offline file data
export interface OfflineFileData {
  id: string;
  cycleNum: number;
  fileData: string; // Base64 encoded file data
  fileName: string;
  fileSize: number;
  fileType: string;
  timestamp: number;
  qualityTourId: string;
  previewUrl: string; // Object URL for preview
}

// Check if device is online (both navigator and Redux state)
const isOnline = (): boolean => {
  const isNavigatorOnline = navigator.onLine;
  const state = store.getState();
  const isReduxOffline = state.appState?.isOfflineStarted || false;
  
  // If Redux indicates offline mode, treat as offline
  // Otherwise, use navigator.onLine
  return !isReduxOffline && isNavigatorOnline;
};

// Helper function to convert File to base64
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

// Helper function to convert base64 to File
export function base64ToFile(base64: string, fileName: string, mimeType: string): File {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || mimeType;
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mime });
}

// Generate a unique ID for files
function generateUniqueId(cycleNum: number): string {
  return `file_${cycleNum}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Store file offline in Redux
export async function storeFileOffline(cycleNum: number, file: File, qualityTourId: string): Promise<void> {
  const uniqueId = generateUniqueId(cycleNum);
  const previewUrl = URL.createObjectURL(file);
  
  try {
    const fileData = await fileToBase64(file);
    
    const offlineFileData: OfflineFileData = {
      id: uniqueId,
      cycleNum,
      fileData,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      timestamp: Date.now(),
      qualityTourId,
      previewUrl
    };
    
    store.dispatch(addOfflineFile(offlineFileData));
    console.log(`File stored offline for Cycle ${cycleNum}:`, file.name);
  } catch (error) {
    console.error('Error converting file to base64:', error);
    throw error;
  }
}

// Get file display data for a cycle
export function getFileDisplayData(cycleNum: number): FileDisplayData[] {
  const state = store.getState();
  const allFiles = state.codeVerification.offlineFiles || [];
  
  return allFiles
    .filter(file => file.cycleNum === cycleNum)
    .map(file => ({
      id: file.id,
      cycleNum: file.cycleNum,
      fileUrl: file.previewUrl, // SharePoint URL for online files, object URL for offline files
      fileName: file.fileName,
      fileSize: file.fileSize,
      uploadTime: file.timestamp,
      isOffline: !!file.fileData // If fileData is empty, it's an online file
    }));
}

// Get all uploaded files
export function getAllUploadedFiles(): FileDisplayData[] {
  const state = store.getState();
  const offlineFiles = state.codeVerification.offlineFiles || [];
  
  return offlineFiles.map(file => ({
    id: file.id,
    cycleNum: file.cycleNum,
    fileUrl: file.previewUrl,
    fileName: file.fileName,
    fileSize: file.fileSize,
    uploadTime: file.timestamp,
    isOffline: true
  }));
}

// SharePoint file upload for Code Verification documents
export async function uploadFileToSharePoint(
  cycleNum: number,
  qualityTourId: string,
  accessToken: string,
  file?: File,
  rowIndex?: number
): Promise<SaveResponse> {
  try {
    let selectedFile = file;
    if (!selectedFile) {
      const fileInput = getCodeVerificationFileInput(cycleNum, rowIndex);
      selectedFile = fileInput?.files?.[0];
    }

    if (!selectedFile) {
      return { success: false, message: "Please select a file before uploading" };
    }

    // Check if offline - store in Redux instead of uploading
    if (!isOnline()) {
      await storeFileOffline(cycleNum, selectedFile, qualityTourId);
      const fileData = getFileDisplayData(cycleNum);
      return { 
        success: true, 
        message: `File "${selectedFile.name}" stored offline for Cycle ${cycleNum}. Will upload when online.`,
        data: fileData
      };
    }

    return await uploadFileToSharePointWithAuth(selectedFile, qualityTourId, cycleNum, accessToken);

  } catch (error) {
    console.error('Upload error:', error);
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    console.error("Upload error details:", {
      errorMessage,
      cycleNum,
      qualityTourId,
      fileName: file?.name,
      fileSize: file?.size
    });
    
    // If upload fails and we have a file, store it offline
    let selectedFile = file;
    if (!selectedFile) {
      const fileInput = getCodeVerificationFileInput(cycleNum, rowIndex);
      selectedFile = fileInput?.files?.[0];
    }
    
    if (selectedFile) {
      try {
        await storeFileOffline(cycleNum, selectedFile, qualityTourId);
        const fileData = getFileDisplayData(cycleNum);
        return { 
          success: true, 
          message: `Upload failed: ${errorMessage}. File "${selectedFile.name}" stored offline for Cycle ${cycleNum}. Will retry when online.`,
          data: fileData
        };
      } catch (offlineError) {
        console.error("Error storing file offline:", offlineError);
        return { 
          success: false, 
          message: `Upload failed: ${errorMessage}. Also failed to store offline. Please check console for details.` 
        };
      }
    }
    
    return { 
      success: false, 
      message: `Upload failed: ${errorMessage}. Please check console for details.` 
    };
  }
}

// Upload file directly from Redux store
export async function uploadFileFromRedux(fileData: OfflineFileData, accessToken: string): Promise<SaveResponse> {
  try {
    const { fileData: base64Data, cycleNum, qualityTourId, fileName, fileType } = fileData;

    if (!base64Data) {
      return { success: false, message: "No file data provided for upload" };
    }

    // Check if offline - return error as file should already be in Redux
    if (!isOnline()) {
      return { 
        success: false, 
        message: "Cannot upload while offline. File will be synced when online." 
      };
    }

    // Convert base64 back to File object
    const file = base64ToFile(base64Data, fileName, fileType);

    return await uploadFileToSharePointWithAuth(file, qualityTourId, cycleNum, accessToken);

  } catch (error) {
    console.error("Upload error:", error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : "Failed to upload file. Please check console for details." 
    };
  }
}

// Core SharePoint upload (aligned with reference API: contextinfo → add file → MERGE metadata)
async function uploadFileToSharePointWithAuth(file: File, qualityTourId: string, cycleNum: number, accessToken: string): Promise<SaveResponse> {
  if (!accessToken) {
    throw new Error('Access token is invalid or missing');
  }

  const digestResponse = await fetch(`${SP_SITE_URL}/_api/contextinfo`, {
    method: 'POST',
    headers: {
      Accept: 'application/json;odata=verbose',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!digestResponse.ok) {
    throw new Error(`Failed to get context info: ${digestResponse.status}`);
  }

  const digestData = await digestResponse.json();
  const formDigestValue = digestData.d.GetContextWebInformation.FormDigestValue;

  // Reference API: Files/add(url='${file.name}',overwrite=true) — encode for spaces/special chars in OData URL
  const encodedName = encodeURIComponent(file.name);

  let uploadResponse: Response | undefined;
  let lastUploadError = '';
  for (const folderPath of UPLOAD_FOLDER_CANDIDATES) {
    const escapedFolder = String(folderPath).replace(/'/g, "''");
    const uploadUrl = `${SP_SITE_URL}/_api/web/GetFolderByServerRelativeUrl('${escapedFolder}')/Files/add(url='${encodedName}',overwrite=true)?$expand=ListItemAllFields`;
    uploadResponse = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json;odata=verbose',
        'X-RequestDigest': formDigestValue,
        Authorization: `Bearer ${accessToken}`,
      },
      body: file,
    });
    if (uploadResponse.ok) break;
    lastUploadError = await uploadResponse.text();
    console.warn(
      'Code Verification: SharePoint Files/add failed for folder',
      folderPath,
      uploadResponse.status,
      lastUploadError
    );
  }

  if (!uploadResponse?.ok) {
    console.error('SharePoint upload failed (all folder paths):', {
      fileName: file.name,
      lastStatus: uploadResponse?.status,
      lastUploadError,
    });
    throw new Error(
      `Upload failed: ${uploadResponse?.status ?? '?'} - ${lastUploadError || 'Unknown error'}`
    );
  }

  const uploadResult = await uploadResponse.json();
  const d = uploadResult?.d;
  const lif = d?.ListItemAllFields;
  const listItemId = lif?.Id ?? lif?.ID;
  const deferredUri = typeof lif?.__deferred?.uri === 'string' ? lif.__deferred.uri : null;

  let resolvedListItemId: number | string | null = listItemId ?? null;
  if (resolvedListItemId == null && deferredUri && accessToken) {
    try {
      const itemRes = await fetch(deferredUri, {
        method: 'GET',
        headers: {
          Accept: 'application/json;odata=verbose',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (itemRes.ok) {
        const itemJson = await itemRes.json();
        const id = itemJson?.d?.Id ?? itemJson?.d?.ID;
        if (id != null) resolvedListItemId = id;
      }
    } catch (e) {
      console.warn('Code Verification: could not follow ListItemAllFields deferred URI', e);
    }
  }

  const serverRelativeUrl =
    d?.ServerRelativeUrl ?? lif?.FileRef ?? lif?.EncodedAbsUrl;

  if (resolvedListItemId == null) {
    console.warn(
      'Code Verification: file may be in library but ListItem id missing; skipping metadata update'
    );
  }

  console.log(`File uploaded (Cycle ${cycleNum}):`, uploadResult);

  if (resolvedListItemId != null) {
    const entityResponse = await fetch(
      `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')?$select=ListItemEntityTypeFullName`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json;odata=verbose',
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!entityResponse.ok) {
      console.warn(
        'Code Verification: get entity type failed; file uploaded without metadata',
        await entityResponse.text()
      );
    } else {
      const entityData = await entityResponse.json();
      const listItemEntityType = entityData.d.ListItemEntityTypeFullName;

      const itemUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items(${resolvedListItemId})`;
      const qid = (qualityTourId || '').replace(/[{}]/g, '');
      const updateBodyFull = {
        __metadata: { type: listItemEntityType },
        QualityId: qid,
        Cycle: `Cycle-${cycleNum}`,
      };

      let updateResponse = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json;odata=verbose',
          'Content-Type': 'application/json;odata=verbose',
          'X-RequestDigest': formDigestValue,
          Authorization: `Bearer ${accessToken}`,
          'IF-MATCH': '*',
          'X-HTTP-Method': 'MERGE',
        },
        body: JSON.stringify(updateBodyFull),
      });

      if (!updateResponse.ok) {
        const errText = await updateResponse.text();
        console.warn(
          'Code verification metadata full update failed, retrying QualityId only:',
          errText
        );
        const updateBodyQ = {
          __metadata: { type: listItemEntityType },
          QualityId: qid,
        };
        updateResponse = await fetch(itemUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json;odata=verbose',
            'Content-Type': 'application/json;odata=verbose',
            'X-RequestDigest': formDigestValue,
            Authorization: `Bearer ${accessToken}`,
            'IF-MATCH': '*',
            'X-HTTP-Method': 'MERGE',
          },
          body: JSON.stringify(updateBodyQ),
        });
        if (!updateResponse.ok) {
          console.warn(
            'Code Verification: metadata update failed (file still in library):',
            await updateResponse.text()
          );
        }
      }
    }
  }

  let sharePointFileUrl = '';
  if (typeof serverRelativeUrl === 'string' && serverRelativeUrl) {
    sharePointFileUrl = buildAbsoluteFileUrl(serverRelativeUrl);
  }
  if (!sharePointFileUrl && d?.ServerRelativeUrl) {
    sharePointFileUrl = buildAbsoluteFileUrl(String(d.ServerRelativeUrl));
  }
  if (!sharePointFileUrl) {
    sharePointFileUrl = buildSpViewerUrl(undefined, file.name);
  }

  const storedFileLabel = file.name;

  const uniqueId = generateUniqueId(cycleNum);
  const fileDisplayData: FileDisplayData = {
    id: uniqueId,
    cycleNum,
    fileUrl: sharePointFileUrl,
    fileName: storedFileLabel,
    fileSize: file.size,
    uploadTime: Date.now(),
    isOffline: false,
  };

  const onlineFileData: OfflineFileData = {
    id: uniqueId,
    cycleNum,
    fileData: '',
    fileName: storedFileLabel,
    fileSize: file.size,
    fileType: file.type,
    timestamp: Date.now(),
    qualityTourId,
    previewUrl: sharePointFileUrl || file.name,
  };

  store.dispatch(addOfflineFile(onlineFileData));

  return {
    success: true,
    message: `File "${file.name}" uploaded successfully for Cycle ${cycleNum}`,
    data: { uploadResult, fileDisplayData, sharePointFileUrl },
  };
}

// Sync all offline files to SharePoint
export async function syncOfflineFilesToSharePoint(accessToken: string): Promise<{ success: boolean; syncedCount: number; errorCount: number }> {
  try {
    const state = store.getState();
    const allFiles = state.codeVerification.offlineFiles || [];
    
    // Only sync files that have fileData (actual offline files)
    // Files with empty fileData are already uploaded online
    const offlineFiles = allFiles.filter(file => file.fileData && file.fileData.length > 0);
    
    if (offlineFiles.length === 0) {
      console.log('No offline files to sync (all files are already uploaded or no files exist)');
      return { success: true, syncedCount: 0, errorCount: 0 };
    }

    if (!isOnline()) {
      console.log('Device is offline, cannot sync files');
      return { success: false, syncedCount: 0, errorCount: offlineFiles.length };
    }

    console.log(`Starting sync of ${offlineFiles.length} offline file(s)...`);
    store.dispatch(setSyncing(true));
    
    let syncedCount = 0;
    let errorCount = 0;

    for (const fileData of offlineFiles) {
      try {
        console.log(`Syncing file: ${fileData.fileName} (Cycle ${fileData.cycleNum})`);
        const result = await uploadFileFromRedux(fileData, accessToken);
        if (result.success) {
          // Remove from offline storage after successful upload
          store.dispatch(removeFile(fileData.id));
          syncedCount++;
          console.log(`Successfully synced file: ${fileData.fileName}`);
        } else {
          console.error(`Failed to sync file ${fileData.fileName}: ${result.message}`);
          errorCount++;
        }
      } catch (error) {
        console.error(`Failed to sync file ${fileData.id} (${fileData.fileName}):`, error);
        errorCount++;
      }
    }

    store.dispatch(setSyncing(false));
    
    console.log(`File sync completed: ${syncedCount} synced, ${errorCount} failed`);
    
    return { 
      success: errorCount === 0, 
      syncedCount, 
      errorCount 
    };
  } catch (error) {
    store.dispatch(setSyncing(false));
    console.error('Error syncing offline files:', error);
    return { success: false, syncedCount: 0, errorCount: 0 };
  }
}

